import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { getSessionId } from '../lib/userParams';
import { Loader2, AlertTriangle, ExternalLink, X } from 'lucide-react';

/**
 * Cloaked redirect. The address bar stays at /track/:slug forever.
 * We fetch the resolved affiliate URL from /api/track-resolve/{slug} and load
 * it inside a full-screen iframe. Because many partners block iframing
 * (X-Frame-Options / CSP) AND browsers fire onLoad for blocked frames anyway,
 * we always surface a small "Not loading? Continue" affordance after a short
 * delay so users are never stuck on a blank screen.
 */
export default function CloakedRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const [state, setState] = useState({ status: 'loading', url: '', error: '' });
  const [showFallbackHint, setShowFallbackHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    document.title = 'Loading offer…';
    let cancelled = false;
    const search = new URLSearchParams(location.search);
    if (!search.get('sid')) search.set('sid', getSessionId());
    api.get(`/track-resolve/${slug}?${search.toString()}`)
      .then((r) => {
        if (cancelled) return;
        setState({ status: 'ready', url: r.data.url, error: '' });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        const kind = err?.response?.data?.error;
        setState({
          status: 'error',
          url: '',
          error: kind === 'disabled'
            ? 'This offer is no longer active.'
            : code === 404
              ? 'This offer link is not recognised.'
              : 'We could not load this offer right now.'
        });
      });
    return () => { cancelled = true; };
  }, [slug, location.search]);

  // Always surface a "Not loading? Continue" hint a few seconds after the iframe
  // is mounted. Cheap safety net for partners that block framing (browsers still
  // fire iframe.onLoad in that case, so we can't rely on it).
  useEffect(() => {
    if (state.status !== 'ready') return;
    const t = setTimeout(() => setShowFallbackHint(true), 3500);
    return () => clearTimeout(t);
  }, [state.status]);

  if (state.status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/40 flex items-center justify-center p-5" data-testid="cloak-error">
        <div className="max-w-md w-full text-center rounded-2xl bg-white border border-slate-200 shadow-xl p-8">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="font-serif-display mt-5 text-2xl font-bold text-slate-900">Offer unavailable</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{state.error}</p>
          <Link to="/" className="mt-6 inline-block text-sm font-semibold text-emerald-700 hover:text-emerald-800">Return to The Loan Alliance</Link>
        </div>
      </div>
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="cloak-loading">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Taking you to your offer…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white" data-testid="cloak-frame">
      <iframe
        src={state.url}
        title="Offer"
        className="w-full h-full border-0"
        allow="clipboard-write; payment"
      />
      {showFallbackHint && !dismissed && (
        <div className="fixed bottom-5 right-5 flex items-center gap-1.5 z-50 shadow-2xl rounded-full">
          <a
            href={state.url}
            rel="noopener noreferrer"
            data-testid="cloak-continue-btn"
            className="inline-flex items-center gap-2 h-11 pl-5 pr-4 rounded-l-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors"
          >
            Not loading? Continue <ExternalLink className="w-4 h-4" />
          </a>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            data-testid="cloak-continue-dismiss"
            className="h-11 w-9 rounded-r-full bg-emerald-700 hover:bg-emerald-800 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
